/**
 * SEO-Konfiguration: Ernährungsstile (slug → DE/EN Inhalte)
 * Jeder Stil generiert eine SEO-Landingpage pro Stadt unter /:style/:city
 *
 * Felder:
 * - slug: URL-Pfadsegment
 * - labelDe/labelEn: Anzeigename
 * - shortDe/shortEn: Eyebrow / Kurzbezeichnung
 * - keywordsDe/keywordsEn: Meta-Keywords-Set
 * - tags: Subset der 36 App-Kriterien, das diesen Stil charakterisiert (DE)
 * - heroDe/heroEn: Hero-H1-Sätze
 * - bodyDe/bodyEn: 2-3 Absätze Long-Form-Content
 * - whatIsDe/whatIsEn: „Was ist X in Stadt?“-Erklärtext
 * - faqsDe/faqsEn: Stil-spezifische FAQs (q/a Paare)
 */
export const SEO_STYLES = [
  {
    slug: "bio",
    labelDe: "Bio",
    labelEn: "Organic",
    shortDe: "Bio-Restaurants",
    shortEn: "Organic Restaurants",
    keywordsDe: "Bio Restaurant, Bio Café, Bio-Bäckerei, Bioland, Demeter, Naturkost, Bio essen gehen",
    keywordsEn: "organic restaurant, organic café, organic bakery, certified organic, eco dining",
    tags: ["Bio-/Naturstandard", "Regionale Zutaten", "Saisonale Zutaten", "Transparente Herkunft", "Frische Zutaten", "Wenige Zusatzstoffe"],
    heroDe: { a: "Bio essen gehen", b: "in {city}." },
    heroEn: { a: "Organic dining", b: "in {city}." },
    bodyDe:
      "Bio-Restaurants in {city} sind mehr als ein Trend — sie sind eine bewusste Entscheidung für Zutaten, die transparent erzeugt werden. Feaststar filtert in {city} gezielt nach zertifizierten Bio-Standards (Bioland, Demeter, EU-Bio) und zeigt nur Restaurants, Cafés und Bäckereien, die ihre Herkunft offenlegen.\n\nStatt einer endlosen Liste siehst du, welcher Betrieb wirklich biologisch arbeitet, welche Zutaten regional bezogen werden und wie frisch zubereitet wird. So findest du in {city} Bio-Restaurants, die zu deinen Standards passen — nicht nur zu deinem Standort.",
    bodyEn:
      "Organic restaurants in {city} are more than a trend — they're a conscious choice for transparently produced ingredients. Feaststar filters {city} by certified organic standards (Bioland, Demeter, EU-Organic) and shows only places that openly disclose their sourcing.\n\nInstead of an endless list, you see which place truly works organically, which ingredients come regionally and how freshly they're prepared. That's how you find organic restaurants in {city} that match your standards — not just your location.",
    whatIsDe:
      "Bio-Restaurants in {city} verwenden Zutaten aus biologischem Anbau — frei von synthetischen Pestiziden, Kunstdüngern und Gentechnik. Feaststar erkennt Betriebe, die mindestens 70% biologisch arbeiten, und zeigt ihre Zertifikate transparent an.",
    whatIsEn:
      "Organic restaurants in {city} use ingredients from biological agriculture — free from synthetic pesticides, fertilizers and GMOs. Feaststar identifies places working at least 70% organically and transparently shows their certifications.",
    faqsDe: [
      {
        q: "Wie finde ich Bio-Restaurants in {city}?",
        a: "Mit Feaststar filterst du in {city} gezielt nach Bio-Standard, Bioland, Demeter und Naturkost. Du siehst auf einen Blick, welche Restaurants, Cafés und Bäckereien wirklich biologisch arbeiten — inklusive Zertifizierungen und Zutatenherkunft.",
      },
      {
        q: "Was ist der Unterschied zwischen Bio-Restaurant und normalem Restaurant in {city}?",
        a: "Bio-Restaurants in {city} verwenden zertifiziert biologische Zutaten, oft auch regional bezogen. Auf Feaststar siehst du den Anteil der Bio-Zutaten, die Zertifikate (EU-Bio, Bioland, Demeter) und Bewertungen der Zutatenqualität.",
      },
      {
        q: "Gibt es Bio-Bäckereien und Bio-Cafés in {city}?",
        a: "Ja — Feaststar listet in {city} nicht nur Bio-Restaurants, sondern auch Bio-Bäckereien, Bio-Cafés, Bio-Konditoreien, Hofläden und Naturkost-Manufakturen. Filter zusätzlich nach Demeter, Bioland oder unverpackt.",
      },
    ],
    faqsEn: [
      {
        q: "How do I find organic restaurants in {city}?",
        a: "With Feaststar you filter {city} for organic standards, Bioland, Demeter and natural food. You see at a glance which restaurants, cafés and bakeries truly work organically — including certifications and ingredient origin.",
      },
      {
        q: "What's the difference between an organic restaurant and a regular one in {city}?",
        a: "Organic restaurants in {city} use certified biological ingredients, often regionally sourced. On Feaststar you see the organic share, certifications (EU-Organic, Bioland, Demeter) and community ratings of ingredient quality.",
      },
      {
        q: "Are there organic bakeries and cafés in {city}?",
        a: "Yes — Feaststar lists in {city} not only organic restaurants, but also organic bakeries, organic cafés, organic confectioneries, farm shops and natural food producers. Filter additionally by Demeter, Bioland or zero-waste.",
      },
    ],
  },
  {
    slug: "vegan",
    labelDe: "Vegan",
    labelEn: "Vegan",
    shortDe: "Vegane Restaurants",
    shortEn: "Vegan Restaurants",
    keywordsDe: "veganes Restaurant, vegan essen, vegan in der Nähe, plant-based, vegan Café",
    keywordsEn: "vegan restaurant, plant-based, vegan near me, vegan café, plant based dining",
    tags: ["Vegan-freundlich", "Zutatenqualität", "Frische Zutaten", "Regionale Zutaten", "Allergen-Transparenz", "Frisch gekocht"],
    heroDe: { a: "Vegan essen gehen", b: "in {city}." },
    heroEn: { a: "Vegan dining", b: "in {city}." },
    bodyDe:
      "Vegane Restaurants in {city} bieten heute mehr als nur die Karten-Beilage. Feaststar zeigt dir in {city} plant-based Cafés, vegane Bäckereien und 100% pflanzliche Restaurants — bewertet nach echter Zutatenqualität, Frische und Regionalität, nicht nur danach, ob „vegan“ draufsteht.\n\nSo findest du in {city} veganes Essen, das wirklich gut ist: handwerklich zubereitet, mit hochwertigen Fetten, ohne industriellen Ersatzkrempel und mit transparenter Herkunft.",
    bodyEn:
      "Vegan restaurants in {city} today offer more than just a sideline on the menu. Feaststar shows you plant-based cafés, vegan bakeries and 100% plant-based restaurants in {city} — rated on real ingredient quality, freshness and regionality, not just because „vegan“ is on the label.\n\nThat's how you find vegan food in {city} that's actually good: handcrafted, with high-quality fats, without industrial substitute clutter and with transparent sourcing.",
    whatIsDe:
      "Vegane Restaurants in {city} verzichten vollständig auf tierische Produkte — Fleisch, Fisch, Milch, Eier, Honig. Feaststar unterscheidet zwischen 100% veganen Betrieben und Restaurants mit vegan-freundlicher Karte, und bewertet die Qualität pflanzlicher Zutaten differenziert.",
    whatIsEn:
      "Vegan restaurants in {city} completely avoid animal products — meat, fish, dairy, eggs, honey. Feaststar distinguishes between fully vegan places and restaurants with vegan-friendly menus, and rates the quality of plant-based ingredients in detail.",
    faqsDe: [
      {
        q: "Wo finde ich gute vegane Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} 100% vegane Restaurants und Cafés mit vegan-freundlicher Karte — sortiert nach Zutatenqualität, Frische und Bewertungen aus der Community. So vermeidest du die typischen plant-based Fast-Food-Fallen.",
      },
      {
        q: "Welche veganen Cafés gibt es in {city}?",
        a: "In {city} listet Feaststar vegane Cafés, vegane Bäckereien und plant-based Brunch-Orte — mit Filtern für Bio, regional, glutenfrei und zuckerarm.",
      },
      {
        q: "Sind alle veganen Restaurants in {city} auch Bio?",
        a: "Nicht zwingend. Auf Feaststar siehst du pro Restaurant in {city} sowohl den veganen Anteil als auch den Bio-Standard — und kannst beides kombinieren, wenn dir beides wichtig ist.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find good vegan restaurants in {city}?",
        a: "Feaststar shows you fully vegan restaurants and cafés with vegan-friendly menus in {city} — sorted by ingredient quality, freshness and community ratings. That way you avoid typical plant-based fast-food traps.",
      },
      {
        q: "Which vegan cafés are there in {city}?",
        a: "In {city} Feaststar lists vegan cafés, vegan bakeries and plant-based brunch spots — with filters for organic, regional, gluten-free and low-sugar.",
      },
      {
        q: "Are all vegan restaurants in {city} also organic?",
        a: "Not necessarily. On Feaststar you see per restaurant in {city} both the vegan share and the organic standard — and you can combine both if both matter to you.",
      },
    ],
  },
  {
    slug: "vegetarisch",
    labelDe: "Vegetarisch",
    labelEn: "Vegetarian",
    shortDe: "Vegetarische Restaurants",
    shortEn: "Vegetarian Restaurants",
    keywordsDe: "vegetarisches Restaurant, vegetarisch essen, vegetarische Küche, ovo-lakto, vegetarier",
    keywordsEn: "vegetarian restaurant, vegetarian dining, lacto-ovo, meat-free",
    tags: ["Vegetarisch-freundlich", "Frische Zutaten", "Regionale Zutaten", "Saisonale Zutaten", "Frisch gekocht", "Hochwertige Fette"],
    heroDe: { a: "Vegetarisch essen", b: "in {city}." },
    heroEn: { a: "Vegetarian dining", b: "in {city}." },
    bodyDe:
      "Vegetarische Restaurants in {city} müssen heute nichts mehr beweisen — sie müssen nur überzeugen. Feaststar filtert in {city} nach vegetarischen und vegetarisch-freundlichen Betrieben, die mit echten Zutaten arbeiten: regional bezogen, saisonal, frisch zubereitet.\n\nDu siehst in {city} sofort, welches Restaurant nicht nur die Beilage tauscht, sondern eine durchdachte vegetarische Karte führt — und welche Cafés und Manufakturen es verdient haben, dass du hingehst.",
    bodyEn:
      "Vegetarian restaurants in {city} don't need to prove anything anymore — they just need to convince. Feaststar filters {city} for vegetarian and vegetarian-friendly places that work with real ingredients: regionally sourced, seasonal, freshly prepared.\n\nYou see right away in {city} which restaurant doesn't just swap a side, but runs a thoughtful vegetarian menu — and which cafés and producers deserve your visit.",
    whatIsDe:
      "Vegetarische Restaurants in {city} verzichten auf Fleisch und Fisch, nutzen aber Milchprodukte und Eier. Feaststar bewertet pro Restaurant, wie tief die vegetarische Karte gedacht ist und wie hochwertig die Milchprodukte sowie Eier bezogen werden.",
    whatIsEn:
      "Vegetarian restaurants in {city} avoid meat and fish but use dairy and eggs. Feaststar rates per restaurant how deeply thought-through the vegetarian menu is and how high-quality the dairy and egg sourcing.",
    faqsDe: [
      {
        q: "Wo finde ich vegetarische Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} sowohl rein vegetarische Restaurants als auch Betriebe mit ausführlicher vegetarischer Karte. Du siehst, wie regional, frisch und durchdacht die fleischlosen Optionen sind.",
      },
      {
        q: "Gibt es vegetarische Cafés und Bäckereien in {city}?",
        a: "Ja — in {city} listet Feaststar vegetarische Cafés, Bäckereien und Manufakturen. Du kannst zusätzlich nach Bio, regional, glutenfrei oder laktosefrei filtern.",
      },
      {
        q: "Was unterscheidet vegetarisch von vegan in {city}?",
        a: "Vegetarische Restaurants in {city} nutzen Milch und Eier, vegane verzichten komplett auf tierische Produkte. Feaststar zeigt dir beide Optionen klar getrennt.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find vegetarian restaurants in {city}?",
        a: "Feaststar shows you in {city} both purely vegetarian restaurants and places with thoroughly vegetarian menus. You see how regional, fresh and thoughtful the meatless options are.",
      },
      {
        q: "Are there vegetarian cafés and bakeries in {city}?",
        a: "Yes — in {city} Feaststar lists vegetarian cafés, bakeries and producers. You can additionally filter by organic, regional, gluten-free or lactose-free.",
      },
      {
        q: "What's the difference between vegetarian and vegan in {city}?",
        a: "Vegetarian restaurants in {city} use dairy and eggs, vegan completely avoid animal products. Feaststar shows both options clearly separated.",
      },
    ],
  },
  {
    slug: "glutenfrei",
    labelDe: "Glutenfrei",
    labelEn: "Gluten-free",
    shortDe: "Glutenfreie Restaurants",
    shortEn: "Gluten-free Restaurants",
    keywordsDe: "glutenfreies Restaurant, glutenfreie Bäckerei, Zöliakie, glutenfrei essen, glutenfrei in der Nähe",
    keywordsEn: "gluten-free restaurant, gluten-free bakery, celiac, gluten free near me",
    tags: ["Glutenfreie Optionen", "Allergen-Transparenz", "Individuell anpassbar", "Frische Zutaten", "Hausgemacht", "Frisch gekocht"],
    heroDe: { a: "Glutenfrei essen", b: "in {city}." },
    heroEn: { a: "Gluten-free dining", b: "in {city}." },
    bodyDe:
      "Glutenfrei in {city} bedeutet mehr als „kann glutenfrei gemacht werden“. Für Menschen mit Zöliakie oder Glutensensitivität entscheidet die Sauberkeit in der Küche, die Trennung der Arbeitsflächen und die Ehrlichkeit der Karte über jeden Restaurantbesuch.\n\nFeaststar filtert in {city} gezielt nach glutenfreien Restaurants, glutenfreien Bäckereien und Cafés mit allergen-transparenter Karte — bewertet von Menschen, die selbst auf Gluten verzichten müssen.",
    bodyEn:
      "Gluten-free in {city} means more than „can be made gluten-free“. For people with celiac disease or gluten sensitivity, kitchen cleanliness, separated workspaces and an honest menu decide every restaurant visit.\n\nFeaststar filters {city} for gluten-free restaurants, gluten-free bakeries and cafés with allergen-transparent menus — rated by people who themselves must avoid gluten.",
    whatIsDe:
      "Glutenfreie Restaurants in {city} bieten Speisen, die nachweislich kein Gluten enthalten — also kein Weizen, Roggen, Gerste, Dinkel oder Hafer (sofern nicht als glutenfrei zertifiziert). Feaststar zeigt zusätzlich an, ob ein Restaurant in {city} eine getrennte Glutenfrei-Küche führt.",
    whatIsEn:
      "Gluten-free restaurants in {city} offer dishes that demonstrably contain no gluten — no wheat, rye, barley, spelt or oats (unless certified gluten-free). Feaststar also indicates whether a restaurant in {city} maintains a separate gluten-free kitchen.",
    faqsDe: [
      {
        q: "Wo finde ich glutenfreie Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} sowohl 100% glutenfreie Restaurants als auch Betriebe mit ausführlicher glutenfreier Karte und allergen-transparenter Information. Du siehst Community-Bewertungen aus Sicht von Zöliakie-Betroffenen.",
      },
      {
        q: "Gibt es glutenfreie Bäckereien in {city}?",
        a: "Ja — Feaststar listet in {city} glutenfreie Bäckereien, Konditoreien und Manufakturen. Du kannst zusätzlich nach Bio, regional und laktosefrei filtern.",
      },
      {
        q: "Sind glutenfreie Restaurants in {city} auch für Zöliakie geeignet?",
        a: "Feaststar markiert in {city}, welche Restaurants Zöliakie-tauglich arbeiten — also mit getrennten Arbeitsflächen, eigenen Pfannen und ohne Kontaminationsrisiko durch Mehlstaub.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find gluten-free restaurants in {city}?",
        a: "Feaststar shows you in {city} both 100% gluten-free restaurants and places with detailed gluten-free menus and allergen-transparent info. You see community ratings from the celiac perspective.",
      },
      {
        q: "Are there gluten-free bakeries in {city}?",
        a: "Yes — Feaststar lists in {city} gluten-free bakeries, confectioneries and producers. You can additionally filter by organic, regional and lactose-free.",
      },
      {
        q: "Are gluten-free restaurants in {city} also celiac-safe?",
        a: "Feaststar marks in {city} which restaurants work celiac-safely — meaning separate workspaces, dedicated pans and no contamination risk from flour dust.",
      },
    ],
  },
  {
    slug: "laktosefrei",
    labelDe: "Laktosefrei",
    labelEn: "Lactose-free",
    shortDe: "Laktosefreie Restaurants",
    shortEn: "Lactose-free Restaurants",
    keywordsDe: "laktosefrei essen, laktoseintoleranz, milchfrei, laktosefreies Café",
    keywordsEn: "lactose-free dining, lactose intolerant, dairy-free, lactose free restaurant",
    tags: ["Laktosefreie Optionen", "Allergen-Transparenz", "Individuell anpassbar", "Vegan-freundlich", "Frische Zutaten", "Frisch gekocht"],
    heroDe: { a: "Laktosefrei essen", b: "in {city}." },
    heroEn: { a: "Lactose-free dining", b: "in {city}." },
    bodyDe:
      "Laktosefrei essen in {city} ist mehr als „Sahne weglassen“. Für Menschen mit Laktoseintoleranz zählt jedes Detail: Welche Saucen sind milchfrei? Welcher Pflanzendrink wird im Kaffee verwendet? Ist die Butter im Brot ausgewiesen?\n\nFeaststar filtert in {city} nach laktosefreien Restaurants, Cafés mit Pflanzenmilch-Alternative und Bäckereien mit milchfreien Backwaren — inklusive Allergen-Transparenz pro Gericht.",
    bodyEn:
      "Lactose-free dining in {city} is more than „skip the cream“. For lactose-intolerant people every detail matters: which sauces are dairy-free? Which plant milk is used in coffee? Is the butter in bread declared?\n\nFeaststar filters {city} for lactose-free restaurants, cafés with plant-milk alternatives and bakeries with dairy-free goods — including allergen transparency per dish.",
    whatIsDe:
      "Laktosefreie Restaurants in {city} bieten Speisen ohne Milchzucker (Laktose). Das kann bedeuten, dass komplett auf Milchprodukte verzichtet wird oder dass laktosefreie Alternativen wie Pflanzenmilch oder laktosefreie Sahne genutzt werden.",
    whatIsEn:
      "Lactose-free restaurants in {city} offer dishes without milk sugar (lactose). This can mean fully avoiding dairy products or using lactose-free alternatives like plant milks or lactose-free cream.",
    faqsDe: [
      {
        q: "Wo finde ich laktosefreie Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} Restaurants und Cafés mit umfangreichen laktosefreien Optionen. Du siehst pro Gericht die Allergen-Information und kannst Pflanzendrink-Optionen filtern.",
      },
      {
        q: "Gibt es laktosefreie Cafés in {city}?",
        a: "Ja — in {city} listet Feaststar Cafés mit Hafer-, Mandel-, Soja- und Kokosmilch, oft auch Bio. Filter zusätzlich nach Zuckerarm oder Glutenfrei.",
      },
      {
        q: "Sind laktosefreie und vegane Restaurants in {city} dasselbe?",
        a: "Nicht ganz — vegan ist immer laktosefrei, aber laktosefrei ist nicht automatisch vegan (z. B. werden oft Eier verwendet). Feaststar zeigt beides klar getrennt für {city}.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find lactose-free restaurants in {city}?",
        a: "Feaststar shows you in {city} restaurants and cafés with extensive lactose-free options. You see allergen info per dish and can filter plant-milk options.",
      },
      {
        q: "Are there lactose-free cafés in {city}?",
        a: "Yes — Feaststar lists in {city} cafés with oat, almond, soy and coconut milk, often organic too. Filter additionally by low-sugar or gluten-free.",
      },
      {
        q: "Are lactose-free and vegan restaurants in {city} the same?",
        a: "Not quite — vegan is always lactose-free, but lactose-free is not automatically vegan (e.g. eggs are often used). Feaststar shows both clearly separated for {city}.",
      },
    ],
  },
  {
    slug: "regional",
    labelDe: "Regional & Saisonal",
    labelEn: "Regional & Seasonal",
    shortDe: "Regionale Restaurants",
    shortEn: "Regional Restaurants",
    keywordsDe: "regionales Restaurant, saisonale Küche, regional essen, kurze Lieferwege, Heimatküche",
    keywordsEn: "regional restaurant, seasonal cuisine, locally sourced, farm-to-table",
    tags: ["Regionale Zutaten", "Saisonale Zutaten", "Transparente Herkunft", "Frische Zutaten", "Manufaktur-Qualität", "Frisch gekocht"],
    heroDe: { a: "Regional & saisonal essen", b: "in {city}." },
    heroEn: { a: "Regional & seasonal dining", b: "in {city}." },
    bodyDe:
      "Regional und saisonal essen in {city} bedeutet kurze Wege, frische Zutaten und echte Bindung an die Region. Feaststar filtert in {city} nach Restaurants, die ihre Lieferanten offenlegen, mit lokalen Höfen arbeiten und ihre Karte am Saisonkalender ausrichten.\n\nSo findest du in {city} Betriebe, die nicht nur „regional“ auf die Karte schreiben, sondern wirklich regional kochen — mit nachvollziehbarer Herkunft und Bewertungen aus der Community.",
    bodyEn:
      "Regional and seasonal dining in {city} means short routes, fresh ingredients and a real connection to the region. Feaststar filters {city} for restaurants that disclose their suppliers, work with local farms and structure their menu by the seasonal calendar.\n\nThat's how you find places in {city} that don't just write „regional“ on the menu but actually cook regionally — with traceable origin and community ratings.",
    whatIsDe:
      "Regionale Restaurants in {city} beziehen den Großteil ihrer Zutaten aus dem näheren Umland — typischerweise innerhalb von 50–150 km. Saisonale Karten ändern sich mehrmals im Jahr, abhängig vom natürlichen Erntekalender.",
    whatIsEn:
      "Regional restaurants in {city} source most ingredients from the nearby surroundings — typically within 50–150 km. Seasonal menus change multiple times a year, depending on the natural harvest calendar.",
    faqsDe: [
      {
        q: "Wie finde ich regionale Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} Restaurants mit transparenter Lieferantenliste — du siehst, von welchen Höfen die Zutaten kommen und wie weit sie transportiert wurden.",
      },
      {
        q: "Was bedeutet saisonal in {city}?",
        a: "Saisonale Küche in {city} bedeutet, dass die Karte sich nach dem regionalen Erntekalender richtet. Im Frühling Spargel und Rhabarber, im Herbst Kürbis und Wild — keine Erdbeeren im Dezember.",
      },
      {
        q: "Gibt es Hofläden und regionale Manufakturen in {city}?",
        a: "Ja — Feaststar listet in {city} nicht nur Restaurants, sondern auch Hofläden, regionale Bäckereien und Manufakturen mit kurzen Lieferwegen.",
      },
    ],
    faqsEn: [
      {
        q: "How do I find regional restaurants in {city}?",
        a: "Feaststar shows you in {city} restaurants with transparent supplier lists — you see which farms ingredients come from and how far they traveled.",
      },
      {
        q: "What does seasonal mean in {city}?",
        a: "Seasonal cuisine in {city} means the menu follows the regional harvest calendar. Asparagus and rhubarb in spring, pumpkin and game in autumn — no strawberries in December.",
      },
      {
        q: "Are there farm shops and regional producers in {city}?",
        a: "Yes — Feaststar lists in {city} not just restaurants, but also farm shops, regional bakeries and producers with short delivery routes.",
      },
    ],
  },
  {
    slug: "paleo",
    labelDe: "Paleo",
    labelEn: "Paleo",
    shortDe: "Paleo Restaurants",
    shortEn: "Paleo Restaurants",
    keywordsDe: "Paleo Restaurant, Paleo essen, Steinzeitküche, getreidefrei, Paleo Diät",
    keywordsEn: "paleo restaurant, paleo diet, primal eating, grain-free dining",
    tags: ["Paleo-orientiert", "Hochwertige tierische Produkte", "Hochwertige Fette", "Frisch gekocht", "Wildfang", "Allergen-Transparenz"],
    heroDe: { a: "Paleo essen", b: "in {city}." },
    heroEn: { a: "Paleo dining", b: "in {city}." },
    bodyDe:
      "Paleo in {city} ist anspruchsvoll — kein Getreide, keine Hülsenfrüchte, keine Milchprodukte, keine industriellen Saatenöle. Feaststar filtert in {city} nach Restaurants, die hochwertige Fette nutzen, mit Weidefleisch oder Wildfang arbeiten und individuell anpassbare Karten führen.\n\nSo findest du in {city} Restaurants und Cafés, in denen Paleo-Esser nicht zum Sonderwunsch werden, sondern wirklich satt nach Hause gehen.",
    bodyEn:
      "Paleo in {city} is demanding — no grains, no legumes, no dairy, no industrial seed oils. Feaststar filters {city} for restaurants using high-quality fats, working with pasture-raised meat or wild catch, and offering individually customizable menus.\n\nThat's how you find restaurants and cafés in {city} where paleo eaters don't become a special request but actually leave satisfied.",
    whatIsDe:
      "Paleo-Restaurants in {city} kochen nach dem Paläolithikum-Prinzip: viel Gemüse, hochwertiges Fleisch und Fisch, gesunde Fette wie Butter, Ghee und Olivenöl — aber kein Getreide, keine Hülsenfrüchte, keine Milchprodukte (außer Butter/Ghee) und kein raffinierter Zucker.",
    whatIsEn:
      "Paleo restaurants in {city} cook by the paleolithic principle: lots of vegetables, high-quality meat and fish, healthy fats like butter, ghee and olive oil — but no grains, no legumes, no dairy (except butter/ghee) and no refined sugar.",
    faqsDe: [
      {
        q: "Wo finde ich Paleo-Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} Restaurants, die getreidefrei kochen, hochwertige Fette nutzen und Paleo-orientierte Gerichte anbieten — meist auch Keto und Low-Carb tauglich.",
      },
      {
        q: "Welche Restaurants in {city} eignen sich für strenge Paleo-Ernährung?",
        a: "Feaststar markiert in {city} Restaurants, die auf industrielle Saatenöle verzichten, mit Weidehaltung arbeiten und Glutenfrei sowie Laktosefrei umfassend bedienen.",
      },
      {
        q: "Sind Paleo und Keto in {city} dasselbe?",
        a: "Nein — Paleo erlaubt Obst und Süßkartoffeln, Keto stark kohlenhydratreduziert. Feaststar zeigt in {city} beide Stile getrennt und kombiniert.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find paleo restaurants in {city}?",
        a: "Feaststar shows you in {city} restaurants cooking grain-free, using high-quality fats and offering paleo-aligned dishes — usually keto and low-carb suitable too.",
      },
      {
        q: "Which restaurants in {city} suit strict paleo?",
        a: "Feaststar marks in {city} restaurants avoiding industrial seed oils, working with pasture-raised animals and serving gluten-free and lactose-free extensively.",
      },
      {
        q: "Are paleo and keto in {city} the same?",
        a: "No — paleo allows fruit and sweet potatoes, keto is heavily carb-restricted. Feaststar shows both styles in {city} separated and combined.",
      },
    ],
  },
  {
    slug: "keto",
    labelDe: "Keto & Low-Carb",
    labelEn: "Keto & Low-Carb",
    shortDe: "Keto Restaurants",
    shortEn: "Keto Restaurants",
    keywordsDe: "Keto Restaurant, Low Carb essen, ketogen, kohlenhydratarm",
    keywordsEn: "keto restaurant, low-carb dining, ketogenic, low carbohydrate",
    tags: ["Keto-freundlich", "Low-Carb geeignet", "Hochwertige Fette", "Hochwertige tierische Produkte", "Proteinreich", "Individuell anpassbar"],
    heroDe: { a: "Keto & Low-Carb essen", b: "in {city}." },
    heroEn: { a: "Keto & low-carb dining", b: "in {city}." },
    bodyDe:
      "Keto und Low-Carb in {city} brauchen Restaurants, die mehr verstehen als „Beilage weg“. Feaststar filtert in {city} nach Restaurants mit echten Low-Carb-Karten, hochwertigen Fetten und proteinreichen Gerichten — ohne versteckte Zucker oder industrielle Soßen.\n\nSo findest du in {city} Orte, an denen Keto kein Sonderwunsch ist, sondern eine echte Option.",
    bodyEn:
      "Keto and low-carb in {city} need restaurants that understand more than „skip the side“. Feaststar filters {city} for restaurants with real low-carb menus, high-quality fats and protein-rich dishes — without hidden sugars or industrial sauces.\n\nThat's how you find places in {city} where keto isn't a special request but an actual option.",
    whatIsDe:
      "Keto-Restaurants in {city} bieten Gerichte mit unter 10g Kohlenhydraten pro Portion, hohem Anteil hochwertiger Fette und ausreichend Protein. Low-Carb-Restaurants gehen typischerweise auf 20–50g Kohlenhydrate pro Mahlzeit.",
    whatIsEn:
      "Keto restaurants in {city} offer dishes with under 10g carbs per portion, a high share of high-quality fats and sufficient protein. Low-carb restaurants typically stay at 20–50g carbs per meal.",
    faqsDe: [
      {
        q: "Wo finde ich Keto-Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} Restaurants mit Low-Carb- und Keto-Karten, hochwertigen Fetten und individuell anpassbaren Gerichten ohne Zucker und Stärke.",
      },
      {
        q: "Gibt es Low-Carb-Cafés in {city}?",
        a: "Ja — Feaststar listet in {city} Cafés mit zuckerarmen Optionen, Bulletproof-Coffee-Stil und proteinreichen Frühstücken.",
      },
      {
        q: "Wie streng sind Keto-Restaurants in {city}?",
        a: "Feaststar zeigt pro Restaurant in {city} an, wie kompromisslos Keto umgesetzt wird — bis hin zu Restaurants, die Kohlenhydrate pro Gericht ausweisen.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find keto restaurants in {city}?",
        a: "Feaststar shows you in {city} restaurants with low-carb and keto menus, high-quality fats and individually customizable dishes without sugar and starch.",
      },
      {
        q: "Are there low-carb cafés in {city}?",
        a: "Yes — Feaststar lists in {city} cafés with low-sugar options, bulletproof-coffee style and protein-rich breakfasts.",
      },
      {
        q: "How strict are keto restaurants in {city}?",
        a: "Feaststar shows per restaurant in {city} how uncompromising keto is implemented — up to restaurants declaring carbs per dish.",
      },
    ],
  },
  {
    slug: "high-protein",
    labelDe: "High Protein",
    labelEn: "High Protein",
    shortDe: "High-Protein Restaurants",
    shortEn: "High-Protein Restaurants",
    keywordsDe: "High Protein Restaurant, eiweißreich essen, Sportler Restaurant, Gym Food",
    keywordsEn: "high protein restaurant, protein-rich dining, gym food, fitness meals",
    tags: ["Proteinreich", "Hochwertige tierische Produkte", "Frisch gekocht", "Individuell anpassbar", "Hochwertige Fette", "Wenige Zusatzstoffe"],
    heroDe: { a: "High-Protein essen", b: "in {city}." },
    heroEn: { a: "High-protein dining", b: "in {city}." },
    bodyDe:
      "High-Protein essen in {city} braucht Restaurants, die mehr verstehen als „Bowl mit Huhn“. Feaststar filtert in {city} nach Restaurants mit hochwertigen tierischen Produkten — Weidehaltung, Wildfang, frisch zubereitet und ohne industrielle Verarbeitungstricks.\n\nFür Sportler, Biohacker und alle, die bewusst auf Eiweißqualität achten, zeigt Feaststar in {city} Restaurants mit echtem Protein-Fokus.",
    bodyEn:
      "High-protein dining in {city} needs restaurants that understand more than „bowl with chicken“. Feaststar filters {city} for restaurants with high-quality animal products — pasture-raised, wild-caught, freshly prepared and without industrial processing tricks.\n\nFor athletes, biohackers and anyone serious about protein quality, Feaststar shows in {city} restaurants with real protein focus.",
    whatIsDe:
      "High-Protein-Restaurants in {city} bieten Gerichte mit mindestens 30g Eiweiß pro Portion — meist aus Fleisch, Fisch, Eiern oder Hülsenfrüchten. Wichtig ist die Qualität der Eiweißquelle, nicht nur die Menge.",
    whatIsEn:
      "High-protein restaurants in {city} offer dishes with at least 30g protein per portion — usually from meat, fish, eggs or legumes. Quality of the protein source matters more than just quantity.",
    faqsDe: [
      {
        q: "Wo finde ich High-Protein-Restaurants in {city}?",
        a: "Feaststar zeigt dir in {city} Restaurants mit hochwertigem Eiweiß — Weidefleisch, Wildfang, frisch zubereitet, individuell anpassbar. Ideal für Sportler und bewusste Esser.",
      },
      {
        q: "Welche Restaurants in {city} eignen sich für Muskelaufbau?",
        a: "Feaststar markiert in {city} Restaurants mit proteinreichen Bowls, hochwertigen tierischen Produkten und ohne versteckte Zucker oder industrielle Soßen.",
      },
      {
        q: "Sind High-Protein-Restaurants in {city} auch Low-Carb?",
        a: "Oft, aber nicht immer — Feaststar zeigt dir in {city} pro Restaurant beide Werte differenziert.",
      },
    ],
    faqsEn: [
      {
        q: "Where do I find high-protein restaurants in {city}?",
        a: "Feaststar shows you in {city} restaurants with high-quality protein — pasture-raised meat, wild catch, freshly prepared, individually customizable. Ideal for athletes and mindful eaters.",
      },
      {
        q: "Which restaurants in {city} suit muscle building?",
        a: "Feaststar marks in {city} restaurants with protein-rich bowls, high-quality animal products and without hidden sugars or industrial sauces.",
      },
      {
        q: "Are high-protein restaurants in {city} also low-carb?",
        a: "Often, but not always — Feaststar shows you in {city} both values per restaurant differentiated.",
      },
    ],
  },
];

export const SEO_STYLE_MAP = Object.fromEntries(SEO_STYLES.map((s) => [s.slug, s]));
export const SEO_STYLE_SLUGS = SEO_STYLES.map((s) => s.slug);
